/**
 * CoiCard — coefficient of inbreeding summary for a hypothetical cross.
 *
 * Shared by HypotheticalPedigreePage (in-app) and SharedPedigreePage (public
 * link), so both render identical numbers and wording.
 *
 * Props:
 *   coi: { coi_percent, generations_analyzed, contributions[] }
 *        (the `coi` field of /api/breeding/hypothetical)
 */
import React from 'react';
import { Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function CoiCard({ coi }) {
  if (!coi) return null;
  const pct = coi.coi_percent || 0;
  const severity = pct >= 12.5 ? 'error' : pct >= 6.25 ? 'warning' : pct > 0 ? 'info' : 'success';
  const headline =
    pct >= 12.5
      ? 'High inbreeding — equivalent to a half-sibling cross or closer'
      : pct >= 6.25
        ? 'Moderate linebreeding — first-cousin equivalent or closer'
        : pct > 0
          ? 'Mild linebreeding'
          : 'No common ancestors detected in 5 generations';

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h4" color={`${severity}.main`}>
            {pct.toFixed(2)}%
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Coefficient of Inbreeding</Typography>
            <Typography variant="caption" color="text.secondary">
              {headline}
            </Typography>
          </Box>
          {/* A hover tooltip does nothing on paper, and the spacer above it
              would drag the icon across the page. The caveat still prints, as
              text, below. */}
          <Tooltip
            className="no-print"
            title={
              `Computed via Wright's path method over ${coi.generations_analyzed} generations of stored pedigree data. ` +
              `This is a lower bound — ancestors' own inbreeding beyond ${coi.generations_analyzed} generations is not accounted for.`
            }
          >
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </Stack>

        {/* Print-only: the tooltip's content, since it cannot be hovered on a
            printed copy that may be the only thing a buyer ever sees. */}
        <Typography
          variant="caption"
          color="text.secondary"
          className="print-only"
          sx={{ display: 'none' }}
        >
          Wright&apos;s path method, {coi.generations_analyzed} generations — a lower bound.
        </Typography>

        {/* Screen only. On paper this list is most of the difference between the
            chart fitting the page below it and spilling onto a second sheet. */}
        {coi.contributions?.length > 0 && (
          <Box className="no-print">
            <Typography variant="caption" color="text.secondary">
              Top contributions:
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {coi.contributions.slice(0, 5).map((c, i) => (
                <Typography variant="body2" key={i}>
                  <strong>{c.ancestor}</strong>: {(c.contribution * 100).toFixed(3)}%
                  &nbsp;
                  <Typography component="span" variant="caption" color="text.secondary">
                    (path length {c.path_length})
                  </Typography>
                </Typography>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
